import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    fetchAssetsByRioPlans,
    fetchMaintenanceDelayed,
    fetchMaintenanceStats,
    fetchVolksTotalAssets,
} from '../../../api/fetchBigNumbers';
import { getUserProfile } from '../../../configuration/login/loginSlice';
import { useRioPlansCache, useVolksTotalCache } from '../../../hooks/useAssets';
import type { Asset } from '../../../models/asset';
import { getAssetIdsByChassisList } from '../../../utils/assetUtils';
import { type MaintenanceStatus, toAssetSummaries } from '../monitoringUtils';
import type { MonitoringPersona } from '../personaConfig';

export interface MonitoringIndicators {
    toSchedule: Set<string>;
    delayed: Set<string>;
    scheduled: Set<string>;
    inMaintenance: Set<string>;
    withService: Set<string>;
    withVolksTotal: Set<string>;
}

export interface MonitoringIndicatorsLoading {
    maintenance: boolean;
    delayed: boolean;
    connectivity: boolean;
    volksTotal: boolean;
}

interface IIndicatorsOptions {
    enabled: boolean;
    assetsLoading: boolean;
}

const emptySet = () => new Set<string>();

const allLoading = (value: boolean): MonitoringIndicatorsLoading => ({
    maintenance: value,
    delayed: value,
    connectivity: value,
    volksTotal: value,
});

const useMonitoringIndicators = (
    persona: MonitoringPersona,
    allAssets: Asset[],
    { enabled, assetsLoading }: IIndicatorsOptions
) => {
    const userProfile = useSelector(getUserProfile);
    const account = userProfile?.account;
    const [toSchedule, setToSchedule] = useState(emptySet);
    const [delayed, setDelayed] = useState(emptySet);
    const [scheduled, setScheduled] = useState(emptySet);
    const [inMaintenance, setInMaintenance] = useState(emptySet);
    const [withService, setWithService] = useState(emptySet);
    const [withVolksTotal, setWithVolksTotal] = useState(emptySet);
    const [loading, setLoading] = useState<MonitoringIndicatorsLoading>(() => allLoading(true));

    useEffect(() => {
        if (!enabled) {
            useRioPlansCache.setState({ cache: {} });
            useVolksTotalCache.setState({ cache: {} });
            for (const reset of [
                setToSchedule,
                setDelayed,
                setScheduled,
                setInMaintenance,
                setWithService,
                setWithVolksTotal,
            ]) {
                reset(emptySet());
            }
            setLoading(allLoading(true));
            return;
        }
        if (!allAssets.length) {
            setLoading(allLoading(assetsLoading));
            return;
        }

        setLoading(allLoading(true));
        let cancelled = false;
        const summaries = toAssetSummaries(allAssets);
        const done = (key: keyof MonitoringIndicatorsLoading) =>
            !cancelled && setLoading(prev => ({ ...prev, [key]: false }));

        fetchMaintenanceStats(persona, allAssets).then(stats => {
            if (cancelled) {
                return;
            }
            setToSchedule(new Set(getAssetIdsByChassisList(allAssets, stats.availableToSchedule)));
            setScheduled(new Set(getAssetIdsByChassisList(allAssets, stats.scheduled)));
            setInMaintenance(new Set(getAssetIdsByChassisList(allAssets, stats.inProgress)));
            done('maintenance');
        });

        fetchMaintenanceDelayed(summaries).then(chassisList => {
            if (cancelled) {
                return;
            }
            setDelayed(new Set(getAssetIdsByChassisList(allAssets, chassisList)));
            done('delayed');
        });

        fetchAssetsByRioPlans(summaries, userProfile ?? undefined).then(byPlan => {
            if (cancelled) {
                return;
            }
            setWithService(new Set(Object.values(byPlan).flat()));
            done('connectivity');
        });

        fetchVolksTotalAssets(summaries).then(([, volksTotalIds]) => {
            if (cancelled) {
                return;
            }
            setWithVolksTotal(new Set(volksTotalIds));
            done('volksTotal');
        });

        return () => {
            cancelled = true;
        };
    }, [persona, allAssets, enabled, assetsLoading, account]);

    const indicators = useMemo<MonitoringIndicators>(
        () => ({ toSchedule, delayed, scheduled, inMaintenance, withService, withVolksTotal }),
        [toSchedule, delayed, scheduled, inMaintenance, withService, withVolksTotal]
    );

    const getStatus = useCallback(
        (assetId: string): MaintenanceStatus | undefined => {
            if (delayed.has(assetId)) {
                return 'atrasada';
            }
            if (inMaintenance.has(assetId)) {
                return 'manut';
            }
            if (scheduled.has(assetId)) {
                return 'agendada';
            }
            if (toSchedule.has(assetId)) {
                return 'agendar';
            }
            return undefined;
        },
        [delayed, inMaintenance, scheduled, toSchedule]
    );

    return { indicators, loading, getStatus };
};

export default useMonitoringIndicators;
